/**
 * WhatsAppShareButton - Inserat-Text + PDF-Exposé per WhatsApp teilen.
 *
 * - Nachricht: nutzt den KI-generierten WhatsApp-Text (descriptions.whatsapp_text),
 *   sonst automatischer Kurztext aus den Fahrzeugdaten. Editierbar.
 * - Optionale Empfängernummer (wird zu wa.me/<nummer> normalisiert, DE-Vorwahl).
 * - "In WhatsApp öffnen": wa.me Link (Handy: App, Desktop: WhatsApp Web).
 * - "PDF-Exposé teilen": mobil über das native Share-Sheet (Web Share API mit Datei),
 *   Desktop-Fallback: PDF-Download + WhatsApp öffnen (Datei im Chat anhängen).
 */
import React, { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Loader2, Copy, FileText, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { fmtEUR, fmtKm } from "@/lib/constants";

const normalizePhone = (raw) => {
  let p = (raw || "").replace(/[^\d]/g, "");
  if (p.startsWith("00")) p = p.slice(2);
  else if (p.startsWith("0")) p = "49" + p.slice(1);
  return p;
};

const buildFallbackText = (vehicle) => {
  const s = vehicle.specs || {};
  const bits = [
    [s.marke, s.modell].filter(Boolean).join(" "),
    s.erstzulassung ? `EZ ${s.erstzulassung}` : null,
    s.kilometerstand != null ? fmtKm(s.kilometerstand) : null,
    s.leistung_ps ? `${s.leistung_ps} PS` : null,
    s.getriebe || null,
  ].filter(Boolean).join(", ");
  const price = vehicle.price != null ? ` Preis: ${fmtEUR(vehicle.price)}.` : "";
  return `${bits}.${price} Bei Interesse melden Sie sich gerne – ich sende Ihnen weitere Fotos und Details.`;
};

export const WhatsAppShareButton = ({ vehicle, size = "sm", variant = "outline", className = "", testId = "whatsapp-share-button", label = "WhatsApp" }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [phone, setPhone] = useState("");
  const [pdfState, setPdfState] = useState("idle"); // idle | loading | ready | error
  const pdfFileRef = useRef(null);

  const defaultText = useMemo(
    () => vehicle?.descriptions?.whatsapp_text || buildFallbackText(vehicle || {}),
    [vehicle]
  );

  const openDialog = () => {
    setMessage(defaultText);
    setOpen(true);
    // PDF im Hintergrund vorladen, damit das Teilen mit einem Tipp klappt
    if (pdfState !== "ready") {
      setPdfState("loading");
      api.get(`/vehicles/${vehicle.id}/export/pdf`, { responseType: "blob" })
        .then((r) => {
          pdfFileRef.current = new File([r.data], `Expose_${vehicle.bestandsnummer || "fahrzeug"}.pdf`, { type: "application/pdf" });
          setPdfState("ready");
        })
        .catch(() => setPdfState("error"));
    }
  };

  const waUrl = () => {
    const p = normalizePhone(phone);
    const base = p ? `https://wa.me/${p}` : "https://wa.me/";
    return `${base}?text=${encodeURIComponent(message.trim())}`;
  };

  const openWhatsApp = () => {
    if (!message.trim()) return toast.warning("Bitte zuerst eine Nachricht eingeben.");
    window.open(waUrl(), "_blank", "noopener");
  };

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(message.trim());
      toast.success("Text kopiert – einfach in WhatsApp einfügen.");
    } catch (e) {
      toast.error("Kopieren nicht möglich – bitte Text manuell markieren.");
    }
  };

  const sharePdf = async () => {
    const file = pdfFileRef.current;
    if (!file) {
      toast.error("Das PDF wird noch vorbereitet – bitte kurz warten.");
      return;
    }
    // Mobil: natives Share-Sheet mit PDF-Datei (Nutzer wählt WhatsApp + Kontakt)
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file], text: message.trim() });
        toast.success("Exposé geteilt.");
        return;
      } catch (e) {
        if (e?.name === "AbortError") return; // Nutzer hat abgebrochen
      }
    }
    // Desktop-Fallback: PDF herunterladen + WhatsApp mit Text öffnen
    const url = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    window.open(waUrl(), "_blank", "noopener");
    toast.info("PDF heruntergeladen – im WhatsApp-Chat über die Büroklammer als Datei anhängen.", { duration: 6000 });
  };

  return (
    <>
      <Button size={size} variant={variant} className={`gap-1.5 h-10 ${className}`} onClick={openDialog} data-testid={testId}>
        <MessageCircle className="h-4 w-4 text-emerald-400" /> {label}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[88vh] overflow-y-auto" data-testid="whatsapp-share-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <MessageCircle className="h-4 w-4 text-emerald-400" /> Per WhatsApp teilen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="wa-message">Nachricht an den Kunden</Label>
              <Textarea id="wa-message" rows={6} value={message} onChange={(e) => setMessage(e.target.value)}
                data-testid="whatsapp-message-textarea" />
              {vehicle?.descriptions?.whatsapp_text ? (
                <p className="text-[10px] text-muted-foreground">Vom KI-Inserat übernommen – vor dem Senden anpassbar.</p>
              ) : (
                <p className="text-[10px] text-muted-foreground">Automatischer Kurztext aus den Fahrzeugdaten – vor dem Senden anpassbar.</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="wa-phone">Empfänger-Nummer (optional)</Label>
              <Input id="wa-phone" type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                placeholder="z.B. 0171 2345678" className="h-11" data-testid="whatsapp-phone-input" />
              <p className="text-[10px] text-muted-foreground">Leer lassen, um den Kontakt direkt in WhatsApp auszuwählen.</p>
            </div>
            <div className="space-y-2">
              <Button className="w-full h-12 gap-2 font-semibold" onClick={openWhatsApp} data-testid="whatsapp-send-button">
                <Send className="h-4 w-4" /> In WhatsApp öffnen
              </Button>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" className="h-11 gap-1.5" disabled={pdfState === "loading"} onClick={sharePdf} data-testid="whatsapp-share-pdf-button">
                  {pdfState === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                  {pdfState === "loading" ? "PDF lädt..." : "PDF-Exposé teilen"}
                </Button>
                <Button variant="outline" className="h-11 gap-1.5" onClick={copyText} data-testid="whatsapp-copy-button">
                  <Copy className="h-4 w-4" /> Text kopieren
                </Button>
              </div>
              {pdfState === "error" && (
                <p className="text-[11px] text-amber-300">Das PDF konnte nicht vorbereitet werden – Text-Versand funktioniert trotzdem.</p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
